import { useCallback, useContext, useEffect, useMemo, useState, type FC } from 'react'
import { createPortal } from 'react-dom'
import * as Sentry from '@sentry/react'
import { Container } from 'react-bootstrap'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { addToast } from '../../store/toast/toastSlice'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { useTracking } from '../../apps/professional-app/fa/common/tracking'
import { useGetAllUsersQuery } from '../../store/user/user'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { DELTA_TRIAL_FEATURES } from '../Plan/DeltaTrial/DeltaTrialFeatures'
import { Upsell } from '../Plan/DeltaTrial/Upsell/Upsell'
import { exportInsightsCsv, InsightsDashboardLayout, createInsightsTrackingClient, DISCUSSION_TAB_CHARTS, type TimeRangeKey } from '../../shared/insights'
import { getDateRange } from '../../shared/insights/utils/insightsTransforms'
import { InsightsWorkflowsMenu } from '../../components/CustomWorkflows/InsightsWorkflowsMenu'
import { UserFilter, type UserOption } from './UserFilter'
import { InsightsNavTabs } from './InsightsNavTabs'
import { useInsightsData } from '../../common/useInsightsData'
import { InsightsFeedback } from './Feedback/InsightsFeedback'
import { useInsightsFeedback } from './hooks/useInsightsFeedback'
import { McpPromoBanners } from '../Growth/McpPromo/McpPromoBanners'
import { McpPromoPage } from '../Growth/McpPromo/mcpPromoConfig'
import { HeaderContext } from '../LayoutWithLeftSidebar/HeaderContext'
import { InsightsDigestMenu } from './InsightsDigest/InsightsDigestMenu'

export const InsightsView: FC = () => {
  useDocumentTitle('Insights')
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { hasQuota, isFetching: quotasFetching, quotaSuccess } = useZocksQuota()
  const { trackingClient: proTracker } = useTracking()
  const { navigateTo, shouldShowInsightsFeedbackCTA, handleDismiss, handleFeedbackClick } = useInsightsFeedback()

  const [timeRange, setTimeRange] = useState<TimeRangeKey>('30D')
  const { portalRef } = useContext(HeaderContext)
  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (portalRef?.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])

  const tracker = useMemo(() => createInsightsTrackingClient((e) => proTracker.track(e), 'firm_account'), [proTracker])
  const { from: fromDate, to: toDate } = getDateRange(timeRange)

  const insightsEnabled = quotaSuccess && hasQuota('insights.enabled.value')

  const { data: usersResp } = useGetAllUsersQuery(
    { accountId, identityTypes: ['idp'] },
    { refetchOnMountOrArgChange: 10, skip: !insightsEnabled || !accountId },
  )
  const users = useMemo<UserOption[]>(
    () => (usersResp?.data ?? []).map((u) => ({ id: u.id, label: getUserDisplayName(u) || u.email || '' })).sort((a, b) => a.label.localeCompare(b.label)),
    [usersResp],
  )

  const { data, isLoading, isFetching, isError } = useInsightsData(accountId, timeRange, {
    userIdList: selectedUserIds.length > 0 ? selectedUserIds : undefined,
    enabled: insightsEnabled,
    charts: DISCUSSION_TAB_CHARTS,
  })

  const onExportCsv = useCallback(async () => {
    if (!data) {
      return
    }
    try {
      await exportInsightsCsv(data, ['discussionTopics', 'investmentProducts', 'clientOutlook', 'assetOpportunities', 'referrals'])
      tracker.track('exported', { format: 'csv' })
    } catch (err) {
      Sentry.captureException(err)
      dispatch(addToast({ variant: 'error', title: 'Error', messages: ['Failed to export insights. Please try again.'] }))
    }
  }, [data, dispatch, tracker])

  if (!quotaSuccess && quotasFetching) {
    return null
  }

  if (!insightsEnabled) {
    return (
      <Container fluid className='d-flex flex-column zocks-gap-1.25 p-0 w-100 full-size-content insights-view'>
        <div className='d-flex flex-grow-1 justify-content-center align-items-center'>
          <Upsell feature={DELTA_TRIAL_FEATURES['insights']} />
        </div>
      </Container>
    )
  }

  return (
    <Container fluid className='d-flex flex-column zocks-gap-3 p-0 w-100 full-size-content insights-view'>
      {headerContainer ? createPortal(<InsightsDigestMenu />, headerContainer) : null}
      <div className='w-100'>
        <InsightsNavTabs active='discussions' />
      </div>
      <InsightsDashboardLayout
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
        onExportCsv={onExportCsv}
        trackingClient={tracker}
        subtitleUserCount={selectedUserIds.length}
        topBanner={<McpPromoBanners page={McpPromoPage.INSIGHTS} />}
        controlsRight={<InsightsWorkflowsMenu fromDate={fromDate} toDate={toDate} userIds={selectedUserIds} />}
        controlsLeft={
          <UserFilter
            users={users}
            selectedUserIds={selectedUserIds}
            onSelectionChange={(ids) => {
              setSelectedUserIds(ids)
              tracker.track('advisor_filter_changed', { advisorCount: ids.length })
            }}
            disabled={isLoading || isFetching}
          />
        }
        feedbackCTA={
          shouldShowInsightsFeedbackCTA ? <InsightsFeedback to={navigateTo} onDismiss={handleDismiss} onFeedbackClick={handleFeedbackClick} /> : null
        }
      />
    </Container>
  )
}
